import {
	format,
	isThisYear,
	parse,
	parseISO,
	differenceInYears,
	differenceInDays,
	addWeeks,
	subWeeks,
	isBefore,
	isAfter,
	isSameDay,
	getYear,
} from 'date-fns';
// import { ja } from 'date-fns/locale';

export function formatYmd(inputDate) {
	// 入力を文字列として扱い、日付にパースする
	const date = parse(inputDate.toString(), 'yyyyMMdd', new Date());

	// 希望の形式 yyyy-MM-dd でフォーマットして返す
	return format(date, 'yyyy-MM-dd');
}

export function jpnDate(dateString) {
	// 文字列を日付オブジェクトに変換
	const date = parseISO(dateString);

	// 年度が今年かどうかを判定
	if (isThisYear(date)) {
		// 今年の場合は年度を省略してフォーマット
		return format(date, 'M月d日(EEE)', { locale: ja });
	} else {
		// 来年の場合は年度を含めてフォーマット
		return format(date, 'yyyy年M月d日(EEE)', { locale: ja });
	}
}

export function slashDate(dateString) {
	// 文字列を日付オブジェクトに変換
	const date = parseISO(dateString);

	// 年度が今年かどうかを判定
	if (isThisYear(date)) {
		// 今年の場合は年度を省略してフォーマット
		return format(date, 'M/d', { locale: ja });
	} else {
		// 来年の場合は年度を含めてフォーマット
		return format(date, 'yyyy/M/d', { locale: ja });
	}
}

export function formatDateAndAge(inputDate) {
	// 入力された日付をDateオブジェクトに変換
	const date = new Date(inputDate);

	// 指定されたフォーマットで日付を出力
	const formattedDate = format(date, 'yyyy年M月d日');

	// 現在の日付と入力された日付の年数の差を計算
	const age = differenceInYears(new Date(), date);

	// オブジェクトを返す
	return {
		format: formattedDate,
		age: age,
	};
}

export function timeOnly(dateTimeString) {
	const date = parseISO(dateTimeString);
	const hours = date.getHours();
	const minutes = date.getMinutes().toString().padStart(2, '0'); // 分はゼロ埋め

	return `${hours}:${minutes}`;
}

/*
 * 指定された日付が指定された日数以内かどうかを判定する
 * @param {string} targetDate - 判定対象の日付（YYYYMMDD 形式）
 * @param {number} daysWithin - 日数
 * @return {boolean} - 指定された日数以内なら false, それ以外は true
 */
export function isDateOutsideRange(targetDate, daysWithin) {
	// 日付を ISO 形式にパース（例: '20241016' や '2024-10-16' の入力形式を許容）
	const parsedDate = targetDate.includes('-')
		? parseISO(targetDate)
		: parseISO(`${targetDate.slice(0, 4)}-${targetDate.slice(4, 6)}-${targetDate.slice(6, 8)}`);

	// 現在の日付を取得
	const today = new Date();

	// 現在の日付と指定された日付の差分を計算（日数）
	const daysDifference = differenceInDays(parsedDate, today);

	// 指定した日数以内なら false, それ以外は true
	return daysDifference > daysWithin;
}

/*
 * 指定された日付が今日から n 週間以内かどうかを判定する
 * @param {string} startDateStr - 判定対象の日付（YYYY-MM-DD 形式）
 * @param {number} weeks - 週数
 * @return {boolean} - 今日から n 週間以内なら true, それ以外は false
 */
export function isTodayWithinNWeeksFrom(startDateStr, weeks) {
	// 指定した日付をDateオブジェクトに変換
	const startDate = new Date(startDateStr);
	// 指定した日付からn週間後の日付を計算
	const endDate = subWeeks(startDate, weeks);

	// 今日の日付が指定日からn週間後以前、またはn週間後と一致する場合にtrueを返す
	return isBefore(new Date(), endDate) || isSameDay(new Date(), endDate);
}

/*
 * 指定された日付が今日から n 週間以降かどうかを判定する
 * @param {string} startDateStr - 判定対象の日付（YYYY-MM-DD 形式）
 * @param {number} weeks - 週数
 * @return {boolean} - 今日から n 週間以降なら true, それ以内は false
 */
export function isTodayBeyondNWeeksFrom(startDateStr, weeks) {
	// 指定した日付をDateオブジェクトに変換
	const startDate = new Date(startDateStr);
	// 指定した日付からn週間後の日付を計算
	const endDate = subWeeks(startDate, weeks);

	// 今日の日付が指定日からn週間後以降であればtrue、以内であればfalseを返す
	return isAfter(new Date(), endDate) || isSameDay(new Date(), endDate);
}

/*
 * 指定日からn週間後の日付を取得する
 * @param {string} startDateStr - 開始日（YYYY-MM-DD 形式）
 * @param {number} weeks - 週数
 * @return {string} - n週間後の日付（YYYY-MM-DD 形式）
 */
export function getDateNWeeksAfter(startDateStr, weeks) {
	// 指定日からn週間後の日付を計算
	const endDate = addWeeks(new Date(startDateStr), weeks);
	// 今年の年を取得
	const currentYear = getYear(new Date());
	// n週間後の日付の年を取得
	const targetYear = getYear(endDate);

	// 年が今年と同じなら 'M/d'、異なるなら 'yyyy/M/d' でフォーマット
	const dateFormat = targetYear === currentYear ? 'M/d' : 'yyyy/M/d';
	return format(endDate, dateFormat);
}

/*
 * 指定日からn週間前の日付を取得する
 * @param {string} startDateStr - 開始日（YYYY-MM-DD 形式）
 * @param {number} weeks - 週数
 * @return {string} - n週間前の日付（YYYY-MM-DD 形式）
 */
export function getDateNWeeksBefore(startDateStr, weeks) {
	// 指定日からn週間前の日付を計算
	const startDate = new Date(startDateStr);
	const targetDate = subWeeks(startDate, weeks);
	// 今年の年を取得
	const currentYear = getYear(new Date());
	// n週間前の日付の年を取得
	const targetYear = getYear(targetDate);

	// 年が今年と同じなら 'M/d'、異なるなら 'yyyy/M/d' でフォーマット
	const dateFormat = targetYear === currentYear ? 'M/d' : 'yyyy/M/d';
	return format(targetDate, dateFormat);
}

export function formatAddYearBr(value) {
	return value.replace(/年/g, '年<br>');
}

export function getCurrentYear() {
	const japanTime = new Date().toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
	return getYear(new Date(japanTime));
}
